/**
 * chain-eos/token-create.ts
 *
 * Create a standard eosio.token-style token on the user's own account:
 * clones the live `eosio.token` contract code straight off the chain
 * (via get_raw_code_and_abi) rather than shipping a vendored wasm blob, so
 * it always matches what's actually deployed and audited on mainnet — then
 * `create` (+ optional `issue`) in the same transaction.
 *
 * Mirrors blocksfinity.com's /wallet/createtoken flow.
 *
 * TRAP — deploy + call in the same transaction ("Decoding error at
 * root<abi>: Read past end of buffer"):
 * WharfKit resolves the ABI for every action's target account from CURRENT
 * chain state (`get_raw_abi`) before it will encode a plain-object action
 * body. `create`/`issue` target `contractAccount`, which has no contract
 * deployed yet at that point — `setcode`/`setabi` only take effect once
 * this transaction lands, not while it's being built client-side. The
 * fetch then gets back an empty ABI blob and `ABI.from()` throws exactly
 * that "root<abi>" decode error deep inside session.transact().
 * Fix: pre-populate the session's ABI cache for this one transaction via
 * `session.transact(args, { abis: [{ account, abi }] })`, using the ABI we
 * already have in hand (the one we just fetched from eosio.token). See
 * `@wharfkit/session`'s `getMergedAbiCache()` — it merges `options.abis`
 * into the cache *before* resolving any action, so the chain fetch for
 * that account never happens. Only do this when we're actually deploying;
 * when `skipDeploy` is true the account's real (possibly different) ABI is
 * already live on chain and should resolve normally.
 *
 * NOT YET LIVE-TESTED end-to-end. Per eos-backend-dev skill's
 * verification.md: before relying on this for a real token, do one
 * deliberate-revert dry run (deploy to a disposable testnet account)
 * before pointing it at mainnet.
 */

import { ABI, Asset, Blob, Bytes, Name } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { EOS_RPC_ENDPOINTS } from './config.js'

const TOKEN_TEMPLATE_ACCOUNT = 'eosio.token'

interface RawCodeAndAbi {
  account_name: string
  wasm: string // base64
  abi: string // base64 — the binary-serialized ABI, exactly what setabi expects
}

interface EosioTokenCode {
  /** setcode's `code` field. */
  wasmBytes: Bytes
  /** setabi's `abi` field. */
  abiBytes: Bytes
  /** Parsed, for pre-populating the session's ABI cache — see the trap note above. */
  abi: ABI
}

/** Fetches the live `eosio.token` wasm+abi straight from the chain. */
export async function fetchEosioTokenCode(): Promise<EosioTokenCode> {
  for (const url of EOS_RPC_ENDPOINTS) {
    try {
      const res = await fetch(`${url}/v1/chain/get_raw_code_and_abi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_name: TOKEN_TEMPLATE_ACCOUNT }),
      })
      if (!res.ok) continue
      const data = (await res.json()) as RawCodeAndAbi
      if (!data.wasm || !data.abi) continue

      const abiBlob = Blob.fromString(data.abi)
      return {
        wasmBytes: Bytes.from(Blob.fromString(data.wasm).array),
        abiBytes: Bytes.from(abiBlob.array),
        abi: ABI.from(abiBlob),
      }
    } catch {
      // try the next endpoint
    }
  }
  throw new Error('Could not fetch eosio.token contract code from chain')
}

/** True if `account` already has contract code deployed (has an ABI). */
export async function accountHasContract(account: string): Promise<boolean> {
  for (const url of EOS_RPC_ENDPOINTS) {
    try {
      const res = await fetch(`${url}/v1/chain/get_raw_abi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_name: account }),
      })
      if (!res.ok) continue
      const data = (await res.json()) as { abi?: string }
      return !!data.abi
    } catch {
      // try the next endpoint
    }
  }
  return false
}

export interface DeployTokenParams {
  /** Account that becomes the token contract (usually the payer/issuer itself). */
  contractAccount: string
  /** Skip setcode/setabi — use when the account already hosts the template contract. */
  skipDeploy?: boolean
  /** Full asset string for the cap, e.g. "1000000.0000 MYTOKEN". */
  maxSupply: string
  /** Account allowed to issue this token. Usually === contractAccount. */
  issuer: string
  /** Optional initial issue, e.g. "1000.0000 MYTOKEN". Omit to skip `issue`. */
  issueAmount?: string
  issueMemo?: string
  /** Which permission on contractAccount/issuer signs — never hardcode 'active'. */
  signingPermission: string
}

export async function deployAndCreateToken(
  session: Session,
  params: DeployTokenParams,
): Promise<string> {
  const contractAccount = params.contractAccount.trim()
  const issuer = params.issuer.trim()
  const auth = [{ actor: contractAccount, permission: params.signingPermission }]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions: any[] = []
  let deployedAbi: ABI | null = null

  if (!params.skipDeploy) {
    const { wasmBytes, abiBytes, abi } = await fetchEosioTokenCode()
    deployedAbi = abi
    actions.push(
      {
        account: 'eosio',
        name: 'setcode',
        authorization: auth,
        data: {
          account: Name.from(contractAccount),
          vmtype: 0,
          vmversion: 0,
          code: wasmBytes,
        },
      },
      {
        account: 'eosio',
        name: 'setabi',
        authorization: auth,
        data: {
          account: Name.from(contractAccount),
          abi: abiBytes,
        },
      },
    )
  }

  actions.push({
    account: contractAccount,
    name: 'create',
    authorization: [{ actor: contractAccount, permission: params.signingPermission }],
    data: {
      issuer: Name.from(issuer),
      maximum_supply: Asset.from(params.maxSupply),
    },
  })

  if (params.issueAmount) {
    actions.push({
      account: contractAccount,
      name: 'issue',
      authorization: [{ actor: issuer, permission: params.signingPermission }],
      data: {
        to: Name.from(issuer),
        quantity: Asset.from(params.issueAmount),
        memo: params.issueMemo ?? '',
      },
    })
  }

  // Only needed when we're deploying in this same transaction (see the trap
  // note at the top of this file) — otherwise the account's real ABI is
  // already live and resolves normally.
  const transactOptions = deployedAbi
    ? { abis: [{ account: Name.from(contractAccount), abi: deployedAbi }] }
    : undefined

  const result = await session.transact({ actions }, transactOptions)
  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}
